import apiClient, { API_PREFIX } from './client';

export interface Conversation {
  conversationId: string;
  userId: string;
  adminId?: string;
  status: string;
  createdAt: string;
  lastMessageAt?: string;
  lastMessagePreview?: string;
  lastMessageSender?: 'user' | 'admin';
  unreadCount?: number;

  // User information (from backend)
  userName?: string;          // Full name for registered users
  phoneNumber?: string;        // Phone number for registered users
  isGuest?: boolean;           // True if user is anonymous guest
  guestId?: string;            // Last 4 chars of user_id for guests
}

export interface ChatMessage {
  messageId: string;
  conversationId: string;
  senderId: string;
  senderType: 'user' | 'admin';
  messageType: 'text' | 'image' | 'order_summary';
  content: string;
  images?: string[];            // Array of image URLs
  timestamp: string;
  status?: string;
}

/**
 * Transform chat image URL to proxy URL
 * 
 * Images can be:
 * 1. S3 key: "chat/abc123/1703001234_xxx.jpg" (new format - needs proxy)
 * 2. Full URL: "https://..." (legacy format or already proxied - use as-is)
 * 
 * The proxy endpoint generates fresh signed URLs on-demand,
 * solving the 1-hour expiration problem.
 */
export const transformChatImageUrl = (imageUrl: string): string => {
  // Already a full URL
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }

  // S3 key format: "chat/{userId}/{filename}"
  if (imageUrl.startsWith('chat/')) {
    const parts = imageUrl.split('/');
    if (parts.length >= 3) {
      const userId = parts[1];
      const filename = parts.slice(2).join('/');
      return `${API_PREFIX}/chat/images/${userId}/${filename}`;
    }
  }

  return imageUrl;
};


export async function fetchConversations(): Promise<Conversation[]> {
  const response = await apiClient.get<{ success: boolean; data: { conversations: any[]; total: number } }>(`${API_PREFIX}/chat/admin/conversations`);
  // Backend wraps response in { success: true, data: { conversations: [...], total: N } }
  // Backend returns already camelCase in ConversationResponse
  const conversations = response.data.data.conversations;
  return conversations.map(conv => ({
    conversationId: conv.id,
    userId: conv.userId,
    adminId: conv.assignedAdminId,
    status: conv.status,
    createdAt: conv.createdAt,
    lastMessageAt: conv.lastMessageAt,
    lastMessagePreview: conv.lastMessagePreview,
    unreadCount: conv.unreadCount,
    userName: conv.userName,
    phoneNumber: conv.phoneNumber,
    isGuest: conv.isGuest || false,
    guestId: conv.guestId,
  }));
}

export async function fetchConversationMessages(conversationId: string): Promise<ChatMessage[]> {
  const response = await apiClient.get<{ success: boolean; data: { messages: ChatMessage[]; total: number } }>(`${API_PREFIX}/chat/conversations/${conversationId}/messages`);
  // Backend wraps response in { success: true, data: { messages: [...], total: N } }
  return response.data.data.messages;
}

export async function sendAdminMessage(conversationId: string, payload: { message?: string; type?: string; imageUrl?: string }): Promise<void> {
  // Map frontend payload to backend format
  const backendPayload = {
    conversationId: conversationId,
    content: payload.message || '',
    type: payload.type || 'text',
    attachmentUrl: payload.imageUrl
  };

  await apiClient.post(`${API_PREFIX}/chat/messages`, backendPayload);
}

export async function uploadChatImage(imageData: string, contentType: string): Promise<{ imageUrl: string; filename: string }> {
  const response = await apiClient.post<{ success: boolean; imageUrl: string; filename: string }>(`${API_PREFIX}/chat/upload-image`, {
    imageData,
    contentType,
  });
  // Backend returns: { success: true, imageUrl: string, filename: string }
  return { imageUrl: response.data.imageUrl, filename: response.data.filename };
}

export async function fetchAdminUnreadCount(): Promise<number> {
  const response = await apiClient.get<{ success: boolean; data: { unreadCount: number } }>(`${API_PREFIX}/chat/admin/unread-count`);
  // Backend wraps response in { success: true, data: { unreadCount: N } }
  return response.data.data.unreadCount;
}

export async function deleteConversation(conversationId: string): Promise<void> {
  await apiClient.delete(`${API_PREFIX}/chat/admin/conversations/${conversationId}`);
}
